
import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import ViewOrderApproval from "../../../components/Modal/Admin/OrderManagement/ViewOrderApproval";
import axiosInstance from "../../../hooks/axiosInstance";
import { toast } from "react-toastify";
import Pusher from "pusher-js";

export default function OrderApproval() {
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({});
  const [showView, setShowView] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [order, setOrder] = useState(null);
  const [orders, setOrders] = useState([]);

  const storedPermissions = localStorage.getItem("userPermissions");
  const userDataString = localStorage.getItem("userData");
  let userTypeId = null;

  if (userDataString) {
    const userData = JSON.parse(userDataString);
    userTypeId = userData.user.user_type_id;
  } else {
    console.log("No user data found");
  }

  const permissions = storedPermissions ? JSON.parse(storedPermissions) : [];

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      try {
        const endpoint = userTypeId === 4 ? "/user/requisition/fetch" : "/user/requisition/super_admin_fetch";
        const orderResponse = await axiosInstance.get(endpoint, { params: { page, filter } });

        let fetchedOrders = orderResponse.data.data.orders.data;
        if (userTypeId === 4) {
          fetchedOrders = fetchedOrders.filter(order => order.approved_status !== 'super_admin_approved');
        }

        const groupedOrders = fetchedOrders.reduce((acc, order) => {
          const key = order.order_code;
          if (!acc[key]) {
            acc[key] = {
              ...order,
              sum_total_price: 0,
              sum_quantity: 0,
            };
          }
          acc[key].sum_total_price += order.total_price;
          acc[key].sum_quantity += order.quantity;
          return acc;
        }, {});

        // order by created_at descending
        const ordersArray = Object.values(groupedOrders).sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );

        setOrders(ordersArray);

        // setOrders(Object.values(groupedOrders));
        setPagination(orderResponse.data.data.orders);
      } catch (error) {
        console.error("Error fetching orders:", error);
        toast.error("Failed to load orders. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [userTypeId, page, filter, refreshKey]);

  const handleShowView = (ord) => {
    if (ord && typeof ord === 'object' && 'id' in ord) { // Assuming 'id' is a key property
      setOrder(ord);
      setShowView(true);
    } else {
      console.error('Invalid order object:', ord);
    }
  };

  const handleClose = useCallback(() => {
    setShowView(false);
  }, []);

  const handleRefresh = useCallback(() => {
    setRefreshKey(prevRefreshKey => prevRefreshKey + 1);
  }, []);

  const handlePageChange = useCallback((newPage) => {
    if (newPage > 0 && newPage <= pagination.last_page) {
      setPage(newPage);
    }
  }, [pagination]);

  const handleFilterChange = useCallback((event) => {
    setFilter(event.target.value);
    setPage(1);
  }, []);

  const getStatusLabel = (status, userId) => {
    if (status === 'super_admin_approved') {
      return <span className="badge bg-success">Approved by Super Admin</span>;
    }
    if (status === 'manager_approved' && userId) {
      return <span className="badge bg-primary">Approved by Manager</span>;
    }
    if (status === "pending") {
      return <span className="badge bg-warning">Pending</span>;
    }
    if (status === null || typeof status === 'undefined') {
      return <span className="badge bg-warning">Pending</span>;
    }
    if (status === "rejected") {
      return <span className="badge bg-danger">Rejected</span>;
    }
    return <span className="badge bg-info">{status}</span>;
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const uniqueOrders = Array.from(new Map(orders.map(order => [order.order_code, order])).values());

  return (
    <>
      {/* Page Title and Breadcrumbs */}
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <h4 className="mb-sm-0" style={{ color: "#c4281c" }}>
              Order Approval
            </h4>
            <div className="page-title-right">
              <ol className="breadcrumb m-0">
                <li className="breadcrumb-item">
                  <Link to="/app/dashboard">Dashboard</Link>
                </li>
                <li className="breadcrumb-item active" style={{ color: "#c4281c" }}>
                  Order Approval
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="row" id="contactList">
        <div className="col-lg-12">
          <div className="card">
            <div className="card-header d-flex align-items-center border-0">
              <h5 className="card-title mb-0 flex-grow-1" style={{ color: "#c4281b" }}>
                Order Approval
              </h5>
            </div>
            <div className="card-body border border-dashed border-end-0 border-start-0">
              <div className="d-flex justify-content-between">
                <div className="col-xl-4 col-md-6">
                  <div className="search-box">
                    <input
                      type="text"
                      className="form-control search"
                      placeholder="Search to product..."
                      value={filter}
                      onChange={handleFilterChange}
                    />
                    <i className="ri-search-line search-icon"></i>
                  </div>
                </div>
              </div>
            </div>
            <div className="card-body">
              <div className="table-responsive table-card">
                <table className="table align-middle table-nowrap table-striped table-borderless table-hover" id="customerTable">
                  <thead className="table-light text-muted">
                    <tr>
                      <th scope="col">#</th>
                      <th scope="col">Company Name</th>
                      <th scope="col">Product Name</th>
                      <th scope="col">Category</th>
                      <th scope="col">Sub-Category</th>
                      <th scope="col">Quantity</th>
                      <th scope="col">Total Price</th>
                      <th scope="col">Order Code</th>
                      <th scope="col">Date Added</th>
                      <th scope="col">Status</th>
                      <th scope="col">Action</th>
                    </tr>
                  </thead>
                  <tbody className="list form-check-all">
                    {uniqueOrders && uniqueOrders.length > 0 ? (
                      uniqueOrders.map((ord, index) => (
                        <tr key={`${ord.order_code}-${index}`}>
                          <td>{index + 1}</td>
                          <td>{ord.company_name}</td>
                          <td>{ord.product_name}</td>
                          <td>{ord.category_name}</td>
                          <td>{ord.sub_category_name}</td>
                          <td>{ord.sum_quantity || ord.quantity}</td>
                          <td>{ord.sum_total_price || ord.total_price}</td>
                          <td>{ord.order_code}</td>
                          <td>{formatDate(ord.created_at)}</td>
                          <td>{getStatusLabel(ord?.approved_status, ord?.user_id)}</td>
                          <td>
                            <div className="dropdown">
                              <button
                                className="btn btn-icon btn-sm fs-16 text-muted dropdown"
                                type="button"
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                              >
                                <i className="mdi mdi-apps"></i>
                              </button>
                              <ul className="dropdown-menu">
                                <li>
                                  <Link
                                    className="dropdown-item"
                                    onClick={() => handleShowView(ord)}
                                  >
                                    <i className="ri-eye-fill text-muted me-2 align-bottom"></i>
                                    View Order Info
                                  </Link>
                                </li>
                              </ul>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="9" className="text-center">No orders found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              <div className="d-flex mt-5 mb-5 flex-wrap">
                <div className="pagination-wrap hstack gap-2 mx-auto">
                  <ul className="pagination">
                    <li className={`page-item ${pagination.current_page === 1 ? "disabled" : ""}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(pagination.current_page - 1)}
                        disabled={pagination.current_page === 1}
                      >
                        Previous
                      </button>
                    </li>
                    {Array.from({ length: pagination.last_page }, (_, index) => index + 1).map((pageNumber) => (
                      <li key={pageNumber} className={`page-item ${pageNumber === pagination.current_page ? "active" : ""}`}>
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(pageNumber)}
                        >
                          {pageNumber}
                        </button>
                      </li>
                    ))}
                    <li className={`page-item ${pagination.current_page === pagination.last_page ? "disabled" : ""}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(pagination.current_page + 1)}
                        disabled={pagination.current_page === pagination.last_page}
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
              </div>
            </div>
          </div>
        </div>
      </div>

       <ViewOrderApproval
        showView={showView}
        order={order}
        closeModal={handleClose}
        refreshPage={handleRefresh}
      />
    </>
  );
}

